import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./Approval.css";
import { FaPlus, FaTimes, FaCheck } from "react-icons/fa";
import AlertModal from "../AlertModal/AlertModal";
import config from "../../config";
import useAuth from "../../Auth/useAuth";

const getUsersUrl = `${config.BASE_URL}/list-users`;
const createRequestUrl = `${config.BASE_URL}/create-request`;
const getRequestDataUrl = `${config.BASE_URL}/list-all-requests`;
const updateRequestStatusUrl = `${config.BASE_URL}/update-request-status`;
const deleteRequestDataUrl = `${config.BASE_URL}/delete-request`;
const updateRequestDataUrl = `${config.BASE_URL}/update-request`;

const Approval = () => {
  const { username, userProfile, setActiveModule } = useAuth();

  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    approver: "",
    actionName: "",
    details: "",
  });

  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    record: null,
  });

  // Add modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  // Modal handler functions
  const showModal = (title, message, type = "info") => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  const closeModal = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  };

  const fetchAllUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        getUsersUrl,
        { userProfile: userProfile },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        },
      );
      console.log("Users:", response.data);
      setUsers(response.data || []);
    } catch (err) {
      console.error("Error fetching users list:", err);
      setError("Failed to load users. Please try again.");
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [userProfile]);

  const fetchAllRequests = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        getRequestDataUrl,
        { username: username, userProfile: userProfile },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        },
      );
      console.log("Requests:", response.data);
      setRequests(response.data || []);
    } catch (err) {
      console.error("Error fetching requests list:", err);
      setError("Failed to load requests. Please try again.");
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  }, [username, userProfile]);

  useEffect(() => {
    fetchAllUsers();
    fetchAllRequests();
  }, [fetchAllUsers, fetchAllRequests]);

  useEffect(() => {
    sessionStorage.setItem("actMod", "approval");
    setActiveModule(sessionStorage.getItem("actMod"));
  }, [setActiveModule]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setError(null);
  };

  const handleAssignment = () => {
    setIsModalOpen(true);
    setFormData({
      approver: "",
      actionName: "",
      details: "",
    });
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRequestSubmit = async () => {
    formData.requestedByUser = username;
    console.log("formData::>", formData);
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await axios.post(createRequestUrl, formData, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      console.log("Request created:", response.data);
      if (!response.status !== 200) {
        showModal('Error', 'Failed to create request. Please try again.', 'error');
        return;
      }
      fetchAllRequests();
      handleCloseModal();
      showModal('Success', 'Request created successfully!', 'success');
    } catch (err) {
      console.error("Error creating request:", err);
      setError("Failed to create request. Please try again.");
      showModal('Error', 'Failed to create request. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusUpdate = async (requestId, status) => {
    console.log("Updating status for request:", requestId, "to:", status);
    try {
      const response = await axios.put(
        updateRequestStatusUrl,
        {
          _id: requestId,
          status: status,
        },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        },
      );

      console.log("Status updated:", response.data);
      if (response.status !== 200) {
        showModal('Error', 'Failed to complete the operation. Please try again.', 'error');
        return;
      }
      showModal('Success', 'Operation completed successfully!', 'success');
      fetchAllRequests();
    } catch (error) {
      console.error("Error updating status:", error);
      showModal('Error', 'Failed to complete the operation. Please try again.', 'error');
    }
  };

  const handleEditRequest = (id) => {
    const request = requests.find((req) => req._id === id);
    if (request && request.status === "Pending") {
      setEditingId(id);
      setEditingData({
        approverRemarks: request.approverRemarks || "",
      });
    }
  };

  const handleCloseEditMode = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleEditingChange = (e) => {
    const { name, value } = e.target;
    setEditingData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveRequest = async (requestId) => {
    console.log("Saving request with ID:", requestId);
    setIsSubmitting(true);
    editingData.id = requestId;
    console.log("Editing data:", editingData);
    try {
      const response = await axios.put(`${updateRequestDataUrl}`, editingData, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data) {
        setRequests(
          requests.map((request) =>
            request._id === requestId
              ? { ...request, ...editingData }
              : request,
          ),
        );
        setEditingId(null);
        fetchAllRequests();
        showModal('Success', 'Request updated successfully!', 'success');
      } else {
        showModal('Error', 'Failed to update request. Please try again.', 'error');
      }
    } catch (error) {
      console.error("Error updating request:", error);
      showModal('Error', 'Failed to update request. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRequest = async (request) => {
    console.log("Delete button clicked for ID:", request);
    setDeleteModal({
      isOpen: true,
      record: request,
    });
  };

  const confirmDelete = async () => {
    const recordId = deleteModal.record?._id;
    if (!recordId) return;

    setIsSubmitting(true);
    try {
      const response = await axios.delete(
        `${deleteRequestDataUrl}/${recordId}`,
        { withCredentials: true },
      );

      if (!response.data) {
        showModal('Error', 'Failed to delete request. Please try again.', 'error');
        return;
      }
      setRequests((prev) => prev.filter((req) => req._id !== recordId));
      setDeleteModal({ isOpen: false, record: null });
      showModal('Success', 'Request deleted successfully!', 'success');
      fetchAllRequests();
    } catch (error) {
      console.error("Error deleting request:", error);
      showModal('Error', 'Failed to delete request. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    const day = String(d.getUTCDate()).padStart(2, "0");
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const year = d.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };

  const renderArgs = (obj) => {
    if (!obj || typeof obj !== "object") return null;

    // Handle single file upload like documents block
    if (obj.field_name && (obj.file_path || obj.start_date || obj.end_date)) {
      return (
        <div style={{ marginTop: "10px" }}>
          <div>
            <strong>field_name:</strong> {obj.field_name}
          </div>
          <div>
            file_path:{" "}
            {obj.file_path ? obj.file_path?.replace(/^upload\//, "") : "N/A"}
          </div>
          <div>start_date: {formatDate(obj.start_date)}</div>
          <div>end_date: {formatDate(obj.end_date)}</div>
        </div>
      );
    }

    const elements = [];

    Object.entries(obj).forEach(([key, value]) => {
      if (key === "documents" && typeof value === "object") {
        Object.entries(value).forEach(([docName, doc]) => {
          elements.push(
            <div key={docName} style={{ marginTop: "10px" }}>
              <div>
                <strong>field_name:</strong> {docName}
              </div>
              <div>
                file_path:{" "}
                {doc?.file_path
                  ? doc.file_path.replace(/^upload\//, "")
                  : "N/A"}
              </div>
              <div>start_date: {formatDate(doc?.start_date)}</div>
              <div>end_date: {formatDate(doc?.end_date)}</div>
            </div>,
          );
        });
        return;
      }

      if (typeof value === "object" && value !== null) {
        elements.push(<div key={key}>{renderArgs(value)}</div>);
      } else {
        elements.push(
          <div key={key}>
            {key}: {value || "N/A"}
          </div>,
        );
      }
    });

    return elements;
  };

  return (
    <div className="approval-container">
      <AlertModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
      />
      {/* You could add a loading indicator here for the initial user fetch too */}
      {isLoading && (
        <div className="spinner-container">
          <div className="spinner"></div>
          <span>Loading approval...</span>
        </div>
      )}
      {!isLoading && userProfile !== "admin" && (
        <div className="approval-system">
          <button className="approval-button" onClick={handleAssignment}>
            <FaPlus /> New Request
          </button>
        </div>
      )}
      {error && !isModalOpen && (
        <div style={{ color: "red", textAlign: "center", marginTop: "1rem" }}>
          {error}
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div
            className={`modal-content ${isSubmitting ? "loading" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Create Request</h3>
              <button
                className="close-button"
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              {error && isModalOpen && (
                <div
                  style={{
                    color: "red",
                    textAlign: "center",
                    marginBottom: "1rem",
                  }}
                >
                  {error}
                </div>
              )}
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="approver">Approver</label>
                  <select
                    id="approver"
                    name="approver"
                    value={formData.approver}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={isSubmitting}
                  >
                    <option value="">Select Approver</option>
                    {isLoading ? (
                      <option disabled>Loading users...</option>
                    ) : users.length > 0 ? (
                      users.map((user) => (
                        <option key={user._id} value={user.username}>
                          {user.username} ({user.profile})
                        </option>
                      ))
                    ) : (
                      <option disabled>No users available</option>
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="actionName">Subject</label>
                  <input
                    type="text"
                    id="actionName"
                    name="actionName"
                    autoComplete="off"
                    value={formData.actionName}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Request details - Using textarea for multi-line input */}
                <div className="form-group full-width">
                  <label htmlFor="details">Details</label>
                  <textarea
                    id="details"
                    name="details"
                    autoComplete="off"
                    value={formData.details}
                    onChange={handleInputChange}
                    className="form-control"
                    rows="4"
                    maxLength={100}
                    disabled={isSubmitting}
                  ></textarea>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      textAlign: "right",
                      marginTop: "4px",
                    }}
                  >
                    {100 - formData.details.length} characters remaining
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn-cancel"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="btn-create"
                  onClick={handleRequestSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="approval-table-wrapper">
        {isLoading && (
          <div className="modal-spinner-overlay">
            <div className="spinner"></div>
            <span>Loading approval data...</span>
          </div>
        )}

        {!isLoading && requests.length > 0 && (
          <div className="approval-scrollable-table">
            <table className="approval-table">
              <thead>
                <tr>
                  <th>Request By</th>
                  <th>Module</th>
                  <th>Operation</th>
                  <th>Content</th>
                  <th>Status</th>
                  <th>Remarks</th>
                  <th>Edit</th>
                  {userProfile === "admin" && <th>Delete</th>}
                </tr>
              </thead>

              <tbody>
                {requests.map((request) => (
                  <tr key={request._id}>
                    <td>{request.requestedByUser}</td>
                    <td>{request.module}</td>
                    <td>{request.action}</td>
                    <td>{renderArgs(request.args)}</td>

                    <td>
                      {userProfile === "admin" ||
                        (userProfile === "accountant" &&
                          request.requestedByUser !== username) ? (
                        request.status === "Pending" ? (
                          <div className="status-btn-container">
                            <button
                              className="approve-btn"
                              onClick={() =>
                                handleStatusUpdate(request._id, "Approved")
                              }
                            >
                              <FaCheck />
                            </button>
                            <button
                              className="reject-btn"
                              onClick={() =>
                                handleStatusUpdate(request._id, "Rejected")
                              }
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ) : (
                          <b>
                            <i
                              style={{
                                color:
                                  request.status === "Approved"
                                    ? "green"
                                    : "red",
                              }}
                            >
                              {request.status}
                            </i>
                          </b>
                        )
                      ) : (
                        <b>
                          <i
                            style={{
                              color:
                                request.status === "Approved"
                                  ? "green"
                                  : request.status === "Rejected"
                                    ? "red"
                                    : "grey",
                            }}
                          >
                            {request.status}
                          </i>
                        </b>
                      )}
                    </td>

                    <td>
                      {userProfile === "admin" &&
                        editingId === request._id &&
                        request.status === "Pending" ? (
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <textarea
                            name="approverRemarks"
                            value={editingData.approverRemarks}
                            onChange={handleEditingChange}
                            className="edit-input"
                            rows="4"
                            maxLength={100}
                          />
                          <div
                            style={{
                              fontSize: "0.8rem",
                              textAlign: "right",
                              marginTop: "4px",
                              color: "#666",
                            }}
                          >
                            {100 - editingData.approverRemarks.length}{" "}
                            characters remaining
                          </div>
                        </div>
                      ) : (
                        request.approverRemarks
                      )}
                    </td>

                    <td>
                      {userProfile === "admin" &&
                        editingId === request._id &&
                        request.status === "Pending" ? (
                        <div className="edit-btn-container">
                          <button
                            className="edit-btn-cancel"
                            onClick={handleCloseEditMode}
                          >
                            Cancel
                          </button>
                          <button
                            className="save-btn"
                            onClick={() => handleSaveRequest(request._id)}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Saving..." : "Save"}
                          </button>
                        </div>
                      ) : (
                        <button
                          className="edit-button"
                          onClick={() =>
                            request.status === "Pending" &&
                            handleEditRequest(request._id)
                          }
                          disabled={request.status !== "Pending"}
                          style={{
                            opacity: request.status !== "Pending" ? 0.5 : 1,
                            cursor:
                              request.status !== "Pending"
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </td>

                    {userProfile === "admin" && (
                      <td>
                        <button
                          className="delete-button"
                          onClick={() => handleDeleteRequest(request)}
                          disabled={request.status !== "Pending"}
                          style={{
                            opacity: request.status !== "Pending" ? 0.5 : 1,
                            cursor:
                              request.status !== "Pending"
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && requests.length === 0 && (
          <div className="no-data-message">No approvals found.</div>
        )}
      </div>

      {deleteModal.isOpen && (
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <div className="delete-modal-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="54"
                height="54"
              >
                <path fill="none" d="M0 0h24v24H0z" />
                <path
                  fill="#ff4757"
                  d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-11.414L9.172 7.757 7.757 9.172 10.586 12l-2.829 2.828 1.415 1.415L12 13.414l2.828 2.829 1.415-1.415L13.414 12l2.829-2.828-1.415-1.415L12 10.586z"
                />
              </svg>
            </div>
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete the request, Subject:
              <span className="trip-id-highlight">
                {" "}
                {deleteModal?.record?.actionName} ?
              </span>
            </p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="delete-modal-buttons">
              <button
                className="cancel-btn"
                onClick={() => setDeleteModal({ isOpen: false, record: null })}
              >
                Cancel
              </button>
              <button className="confirm-delete-btn" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Approval;
